import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { GetBillTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setFulfillmentMethod from "@insite/client-framework/Store/Context/Handlers/SetFulfillmentMethod";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getBillTosDataView } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import translate from "@insite/client-framework/Translate";
import { BillToModel, WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import loadWarehouse from "@WhiteCap/Handlers/WarehouseHandler/LoadWarehouse";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@WhiteCap/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    noShipToAndCantCreate: boolean;
    setParameter: (parameter: GetBillTosApiParameter) => void;
    parameter: GetBillTosApiParameter;
    billTo?: BillToModel;
    onSelect: (billTo: BillToModel) => void;
    extendedStyles?: ChangeCustomerBillToSelectorStyles;
    isLoading?: boolean;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    session: state.context.session,
    billTosDataView: getBillTosDataView(state, props.parameter),
    customerSettings: getSettingsCollection(state).customerSettings,
    fulfillmentMethod: state.components.addressDrawer.fulfillmentMethod,
});

const mapDispatchToProps = {
    loadBillTos,
    loadWarehouse,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ChangeCustomerBillToSelectorStyles {
    billToSelectorDynamicDropdown?: DynamicDropdownPresentationProps;
}

export const changeCustomerBillToSelectorStyles: ChangeCustomerBillToSelectorStyles = 
{
    billToSelectorDynamicDropdown : {
         cssOverrides: {
            selectedText: css`
                           font-weight:500;
                        `,
            option: css`
                       font-weight:500;
                    `,
            additionalText: css` 
                              display:flex;
                              font-weight:400;
                            `,
            inputSelect: css` 
            ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css` height:60px; `,
                            null,
                            null,
                        ],
                        "min",
                    )}`,
            dropdownWrapper: css`
                   [class^="PopoverStyle"] {
                      top : 58px;
                   }
            `                
         },
    }
};

const ChangeCustomerBillToSelector: FC<Props> = ({
    noShipToAndCantCreate,
    parameter,
    setParameter,
    billTo,
    onSelect,
    extendedStyles,
    isLoading,
    billTosDataView,
    session,
    loadWarehouse,
    fulfillmentMethod,
}) => {
    const [styles] = useState(() => mergeToNew(changeCustomerBillToSelectorStyles, extendedStyles));
    const [options, setOptions] = useState<OptionObject[]>([]);

    useEffect(() => {
        if (billTosDataView.value) {
            const options: OptionObject[] = billTosDataView.value.map(billTo => {
                return {
                    optionText: generateBillToLabel(billTo),
                    optionValue: billTo.id,
                    additionalText: `${billTo.companyName} | ${billTo.fullAddress}`,
                };
            });

            // Check if the current billTo is not already in the options list
            const billToAlreadyExists = options.some(option => option.optionValue === billTo?.id);

            // If not, add it to the options
            if (billTo && !billToAlreadyExists) {
                options.push({
                    optionText: generateBillToLabel(billTo),
                    optionValue: billTo.id,
                    additionalText: `${billTo.companyName} | ${billTo.fullAddress}`,
                });
            }
            setOptions(options);
        }
    }, [billTosDataView, billTo]);

    const generateBillToLabel = (billTo: BillToModel) => {
        const words = billTo.label.split(" ");
        words.splice(0, words.length);
        words.unshift(billTo?.properties["customerName"]);
        words.unshift(billTo.customerNumber);
        return words.join(" | ");
    };

    const searchTextChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setParameter({
            ...parameter,
            filter: event.currentTarget.value,
        });
    };

    const selectCustomerHandler = (billToId?: string) => {
        if (billTosDataView.value && billToId) {
            let selectedBillTo: BillToModel | undefined = billTosDataView.value.find(billTo => billTo.id === billToId);

            if (!selectedBillTo && billTo?.id === billToId) {
                selectedBillTo = billTo;
            }

            // const billToSelectedWarehouse = loadWarehouse(billTo?.properties?.defaultWarehouse)
            if (selectedBillTo?.properties?.defaultWarehouse) {
                loadWarehouse({
                    warehouseId: selectedBillTo?.properties?.defaultWarehouse,
                    changeShipWarehouse: true,
                });
            }
            // .onComplete((warehouse: WarehouseModel) => {

            // });
            // console.log("billToWarehouse", billToSelectedWarehouse)
            // if(billToSelectedWarehouse != null){
            //     console.log("Inside billToSelectedWarehouse", billToSelectedWarehouse);
            //     setBillToWarehouse(billToSelectedWarehouse);
            // }

            if (selectedBillTo) {
                onSelect(selectedBillTo as BillToModel);
            }
        }
    };

    const toasterContext = React.useContext(ToasterContext);
    const fulfillmentMethodChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setFulfillmentMethod({ fulfillmentMethod: event.currentTarget.value as "Ship" | "PickUp" });
    };

    const handlePickUpAddressChange = async (pickUpWarehouse: WarehouseModel) => {
        await updatePickUpWarehouse({ pickUpWarehouse });
        toasterContext.addToast({
            body: translate("Pick Up Address Updated"),
            messageType: "success",
        });
    };

    return (
        <>
            <DynamicDropdown
                label={translate("Account / Ship-To Jobsite / Address")}
                {...styles.billToSelectorDynamicDropdown}
                onSelectionChange={selectCustomerHandler}
                onInputChange={searchTextChanged}
                selected={billTo?.id}
                options={options}
                placeholder={translate("Search or Select Bill To")}
                isLoading={isLoading}
                error={noShipToAndCantCreate && siteMessage("SignIn_NoShipToAndCantCreate")}
                data-test-selector="changeCustomerBillToSelector"
                filterOption={() => true}
            />
            {/* <GridContainer>            
            <GridItem >
                {(session.fulfillmentMethod === FulfillmentMethod.PickUp || session.fulfillmentMethod === FulfillmentMethod.Ship) && billToWarehouse && (
                    <WarehouseAddressInfoDisplay warehouse={billToWarehouse} />
                )}
            </GridItem>
        </GridContainer> */}
        </>
    );
};

// interface WarehouseAddressInfoDisplayProps {
//     warehouse: WarehouseModel;
// }

// const WarehouseAddressInfoDisplay: FC<WarehouseAddressInfoDisplayProps> = ({ warehouse }) => {

//     return (
//         <StyledWrapper>
//             <Typography data-test-selector="changeCustomer_warehouseName">
//                 {warehouse.description || warehouse.name}
//             </Typography>
//             <AddressInfoDisplay
//                 address1={warehouse.address1}
//                 address2={warehouse.address2}
//                 city={warehouse.city}
//                 state={warehouse.state}
//                 postalCode={warehouse.postalCode}
//                 phone={warehouse.phone}
//             />
//         </StyledWrapper>
//     );
// };

export default connect(mapStateToProps, mapDispatchToProps)(ChangeCustomerBillToSelector);
